import { cloneElement } from "react";
import clsx from "clsx";

import styles from "./Form.module.scss";

export type FormElementProps<IdType> = {
  id: IdType;
  label: string;
  required?: boolean;
  showRequiredIndicator?: boolean;
  showOptionalIndicator?: boolean;
  error?: string;
  children: React.ReactElement;
};

// Accepts an IDType to strongly type the ID
export function FormElement<IdType extends string>({
  id,
  label,
  error,
  required = false,
  showRequiredIndicator = true,
  showOptionalIndicator = false,
  children,
}: FormElementProps<IdType>) {
  const errorDivId = `${id}-error`;
  const hasError = Boolean(error);
  // Add id and aria-required props to children
  const enhancedChild = cloneElement(children, {
    id,
    // Deliberately using aria-required instead of required because we don't want
    // the browser's native required validation to fire. This way, validation is handled
    // in a consistent manner.
    "aria-required": `${required}`,
    "aria-invalid": `${hasError}`,
    "aria-errormessage": errorDivId,
  });
  return (
    <div
      className={clsx(
        "form-element form-element_small",
        hasError && "has-error"
      )}
    >
      <label className="form-element__label" htmlFor={id}>
        {required && showRequiredIndicator && (
          <span title="Required" className="required">
            *{" "}
          </span>
        )}
        {label}
        {!required && showOptionalIndicator && (
          <span title="Optional" className={styles["ap-optional-label"]}>
            Optional
          </span>
        )}
      </label>
      {enhancedChild}
      {error && (
        <div role="alert" id={errorDivId} className="form-element__help">
          {error}
        </div>
      )}
    </div>
  );
}
